import { useCallback, useEffect, useRef, useState } from "react";
import type { AbstractAgent } from "@ag-ui/client";
import { UserSimStreamRequest } from "api/userSimApi";
import { AutoFillValues } from "logic/autoFill";

export type AutoFillPhase = "idle" | "running" | "canceling" | "done" | "error";

export type AutoFillStepStatus = "running" | "done";

// 진행 단계 (AG-UI STEP_STARTED/FINISHED) 와 그 단계에서 쓴 툴 (TOOL_CALL_START/END)·생각 요약 (REASONING_MESSAGE_*)
export type AutoFillStep = {
  name: string;
  label: string;
  status: AutoFillStepStatus;
  startedAt: number;
  tools: { id: string; label: string; status: AutoFillStepStatus }[];
  // 이 단계의 마지막 생각 요약 글
  reasoning?: string;
};

// Agent 가 60초 동안 아무 이벤트도 보내지 않으면 실패로 본다 (계약 3장 keepalive)
export const AUTO_FILL_IDLE_TIMEOUT_MS = 60_000;

// 요청 직전 프론트가 하는 단계 (칸 설계도 만들기)
const PREPARE_STEP = "prepare";

const labelOf = (
  event: { metadata?: Record<string, any> },
  fallback: string
) =>
  typeof event.metadata?.label === "string" ? event.metadata.label : fallback;

// 요청을 만든 뒤 user_sim Agent 를 실행해(AG-UI) 진행 단계·지금 쓰는 칸·칸 값을 모은다.
export default function useAutoFillStream() {
  const [phase, setPhase] = useState<AutoFillPhase>("idle");
  const [request, setRequest] = useState<UserSimStreamRequest>();
  const [steps, setSteps] = useState<AutoFillStep[]>([]);
  // 지금 쓰고 있는 칸 (마지막 STATE_DELTA 의 칸)
  const [currentKey, setCurrentKey] = useState<string>();
  const [values, setValues] = useState<AutoFillValues>({});
  const [error, setError] = useState<string>();

  const agentRef = useRef<AbstractAgent>();
  const canceledRef = useRef(false);
  const runRef = useRef<Promise<void>>();

  const start = useCallback(
    (getRequest: () => Promise<UserSimStreamRequest>) => {
      canceledRef.current = false;
      agentRef.current = undefined;
      setPhase("running");
      setRequest(undefined);
      setSteps([
        {
          name: PREPARE_STEP,
          label: "활동지 정리",
          status: "running",
          startedAt: Date.now(),
          tools: [],
        },
      ]);
      setCurrentKey(undefined);
      setValues({});
      setError(undefined);

      const updateStep = (
        name: string,
        update: (step: AutoFillStep) => AutoFillStep
      ) =>
        setSteps((prev) => {
          // 같은 이름이 여러 번 열리면 마지막 것
          const index = prev.map((step) => step.name).lastIndexOf(name);
          return index < 0
            ? prev
            : prev.map((step, i) => (i === index ? update(step) : step));
        });

      // 진행 중인 마지막 단계
      const updateRunningStep = (
        update: (step: AutoFillStep) => AutoFillStep
      ) =>
        setSteps((prev) => {
          const index = prev.map((step) => step.status).lastIndexOf("running");
          return index < 0
            ? prev
            : prev.map((step, i) => (i === index ? update(step) : step));
        });

      runRef.current = (async () => {
        let finished = false;
        let failure: string | undefined;
        let idleTimer: ReturnType<typeof setTimeout> | undefined;
        try {
          const req = await getRequest();
          if (canceledRef.current) return;
          setRequest(req);
          updateStep(PREPARE_STEP, (step) => ({ ...step, status: "done" }));

          const { createUserSimAgent, fromValuePath } = await import(
            "api/userSimAgent"
          );
          if (canceledRef.current) return;
          const agent = createUserSimAgent(req);
          agentRef.current = agent;

          const armIdle = () => {
            clearTimeout(idleTimer);
            idleTimer = setTimeout(() => {
              failure = "Agent 응답이 없어요. 다시 시도해 주세요";
              agent.abortRun();
            }, AUTO_FILL_IDLE_TIMEOUT_MS);
          };
          armIdle();

          await agent
            .runAgent(
              {},
              {
                onEvent: armIdle,
                onStepStartedEvent: ({ event }) =>
                  setSteps((prev) => [
                    ...prev,
                    {
                      name: event.stepName,
                      label: labelOf(event, event.stepName),
                      status: "running",
                      startedAt: Date.now(),
                      tools: [],
                    },
                  ]),
                onStepFinishedEvent: ({ event }) =>
                  updateStep(event.stepName, (step) => ({
                    ...step,
                    status: "done",
                  })),
                onToolCallStartEvent: ({ event }) =>
                  updateRunningStep((step) => ({
                    ...step,
                    tools: [
                      ...step.tools,
                      {
                        id: event.toolCallId,
                        label: labelOf(event, event.toolCallName),
                        status: "running",
                      },
                    ],
                  })),
                onToolCallEndEvent: ({ event }) =>
                  setSteps((prev) =>
                    prev.map((step) => ({
                      ...step,
                      tools: step.tools.map((tool) =>
                        tool.id === event.toolCallId
                          ? { ...tool, status: "done" }
                          : tool
                      ),
                    }))
                  ),
                // 생각 요약 — 단계의 마지막 글. reasoningMessageBuffer 는 이번 delta 를 더하기 전 글이라,
                // 새 글의 첫 delta 가 오면 이전 글을 바꾼다 (그 전까지는 이전 글을 둬 높이가 출렁이지 않게)
                onReasoningMessageContentEvent: ({
                  event,
                  reasoningMessageBuffer,
                }) =>
                  updateRunningStep((step) => ({
                    ...step,
                    reasoning: reasoningMessageBuffer + event.delta,
                  })),
                onStateDeltaEvent: ({ event }) => {
                  const op = event.delta[event.delta.length - 1];
                  const key = op && fromValuePath(op.path);
                  if (key) setCurrentKey(key);
                },
                onStateChanged: ({ state }) =>
                  setValues({ ...(state?.values || {}) }),
                onRunFinishedEvent: () => {
                  finished = true;
                },
                // RUN_ERROR 를 받아도 runAgent 는 정상 종료한다 — 직접 기록
                onRunErrorEvent: ({ event }) => {
                  failure ??= event.message;
                },
                onRunFailed: ({ error }) => {
                  failure ??= error.message;
                },
              }
            )
            .catch((e: unknown) => {
              failure ??= e instanceof Error ? e.message : String(e);
            });

          if (canceledRef.current) return;
          // RUN_FINISHED 없이 끝나도 runAgent 는 정상 종료한다
          if (!failure && !finished) failure = "응답이 중간에 끊겼어요";
          if (failure) throw new Error(failure);
          setPhase("done");
        } catch (e) {
          if (canceledRef.current) return;
          setError(e instanceof Error ? e.message : String(e));
          setPhase("error");
        } finally {
          clearTimeout(idleTimer);
        }
      })();
    },
    []
  );

  // 요청·스트림을 완전히 멈춘 뒤 닫는다
  const cancel = useCallback(async () => {
    setPhase("canceling");
    canceledRef.current = true;
    agentRef.current?.abortRun();
    await runRef.current;
    setPhase("idle");
  }, []);

  const reset = useCallback(() => setPhase("idle"), []);

  useEffect(
    () => () => {
      canceledRef.current = true;
      agentRef.current?.abortRun();
    },
    []
  );

  return {
    phase,
    request,
    steps,
    currentKey,
    values,
    error,
    start,
    cancel,
    reset,
  };
}
